import type {
	ChangePasswordRequest,
	ChangePasswordResponse,
	LoginRequest,
	LoginResponse,
	LoginSuccess,
	MeResponse,
	MfaVerifyRequest,
	RoleCode,
	SwitchRoleResponse,
} from "@seagames/contracts";
import { queryOptions } from "@tanstack/react-query";
import { api, apiPublic } from "../api/client";
import { queryClient } from "../api/query-client";
import { NOTION_SIGN_IN_ROLE } from "./access";
import { clearTokens, setTokens } from "./token-store";

/**
 * `/auth/me` is the single source of truth for who is signed in and which
 * ONE role is active. Never decoded from the JWT, never a union of held roles.
 */
export const meQueryOptions = queryOptions({
	queryKey: ["auth", "me"] as const,
	queryFn: ({ signal }) => api<MeResponse>("GET", "/auth/me", { signal }),
	staleTime: 5 * 60_000,
});

export function acceptLogin(result: LoginSuccess): void {
	setTokens({ accessToken: result.access_token, refreshToken: result.refresh_token });
	queryClient.removeQueries({ queryKey: meQueryOptions.queryKey });
}

/**
 * WSL Notion always signs in AS SUPER_ADMIN (`NOTION_SIGN_IN_ROLE`): a user
 * holding other roles too would otherwise get whichever the server picks and
 * maybe the no-access screen. A login without the role is refused with
 * ROLE_NOT_HELD — the form says so in its own words.
 */
export const login = (body: LoginRequest) =>
	apiPublic<LoginResponse>("POST", "/auth/login", { body: { ...body, role: NOTION_SIGN_IN_ROLE, client_app: "NOTION" } satisfies LoginRequest });

/** Finish an MFA sign-in. The ticket stands in for the session that does not exist yet. */
export const verifyMfa = (body: MfaVerifyRequest) => apiPublic<LoginSuccess>("POST", "/auth/mfa/verify", { body });

export async function changePassword(body: ChangePasswordRequest): Promise<void> {
	const result = await api<ChangePasswordResponse>("POST", "/auth/change-password", { body });
	// The token without the `pwc` claim — the server's gate opens here.
	setTokens({ accessToken: result.access_token });
	await queryClient.invalidateQueries({ queryKey: meQueryOptions.queryKey });
}

export async function switchRole(role: RoleCode): Promise<void> {
	const result = await api<SwitchRoleResponse>("POST", "/auth/switch-role", { body: { role } });
	setTokens({ accessToken: result.access_token });
	// Everything cached was read under the OLD role's permissions.
	queryClient.clear();
}

export async function logout(): Promise<void> {
	try {
		await api<null>("POST", "/auth/logout");
	} catch {
		// Abandoning the session either way; a failed call must not strand the UI.
	}
	clearTokens("user");
	queryClient.clear();
}
